import { useState } from "react";
import { api } from "../../lib/api";
import { useGameStore } from "../../store/gameStore";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Clock, Swords, UserPlus, SkipForward, X } from "lucide-react";
import { InfoTip } from "./InfoTip";

function formatClock(minutes, day) {
  const total = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = Math.floor(total % 60);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `Day ${day} — ${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function FreeRoamGmPanel({ game, tokens }) {
  const freeRoam = useGameStore((s) => s.free_roam);
  const [pullNpcTarget, setPullNpcTarget] = useState({});

  const isRaid = !!game.raid_id;
  const isMasterGm = isRaid && !game.my_branch_id;
  const isScopedGeneral = isRaid && !!game.my_branch_id;

  const toggle = (enabled) => api.patch(`/games/${game.id}/free-roam`, { enabled }).catch(() => toast.error("Could not update Free Roam"));
  const setPace = (pace) => api.patch(`/games/${game.id}/free-roam`, { pace }).catch(() => toast.error("Could not change pace"));
  const setClock = (h, m) => api.patch(`/games/${game.id}/free-roam`, { clock_minutes: h * 60 + m }).catch(() => toast.error("Could not update the clock"));
  const setClockDay = (day) => api.patch(`/games/${game.id}/free-roam`, { clock_day: Math.max(1, day) }).catch(() => toast.error("Could not update the clock"));
  const advanceTime = (minutesToAdd) => {
    if (!freeRoam) return;
    let total = freeRoam.clock_minutes + minutesToAdd;
    let day = freeRoam.clock_day;
    while (total >= 1440) { total -= 1440; day += 1; }
    api.patch(`/games/${game.id}/free-roam`, { clock_minutes: total, clock_day: day }).catch(() => toast.error("Could not advance time"));
  };

  const pullNpc = async (encounterId) => {
    const tokenId = pullNpcTarget[encounterId];
    const token = tokens.find((t) => t.id === tokenId);
    if (!token) return toast.error("Pick an NPC token first");
    await api.post(`/games/${game.id}/free-roam/encounters/${encounterId}/pull-npc`, { token_id: token.id, name: token.name });
  };

  const nextTurn = (encounterId) => api.post(`/games/${game.id}/free-roam/encounters/${encounterId}/next-turn`);
  const endEncounter = (encounterId) => api.post(`/games/${game.id}/free-roam/encounters/${encounterId}/end`);

  if (!freeRoam) return null;
  const npcTokens = tokens.filter((t) => t.token_type === "npc");

  return (
    <div className="space-y-2 border-t border-white/10 pt-3 mt-1">
      {isRaid && (
        <div className="space-y-2 bg-black/20 border border-[#D4AF37]/20 rounded-xl p-3" data-testid="raid-campaign-clock-panel">
          <p className="text-xs text-[#f1e0c5]/70 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-[#D4AF37]" /> Campaign Clock <InfoTip topic="raid_campaign_clock" />
          </p>
          <div className="text-sm text-[#D4AF37] font-medium" data-testid="raid-campaign-clock-display">
            {formatClock(freeRoam.clock_minutes, freeRoam.clock_day)}
          </div>
          {isMasterGm ? (
            <>
              <div className="flex items-center gap-2">
                <Input
                  key={`raid-clock-hour-${freeRoam.clock_minutes}-${freeRoam.clock_day}`}
                  data-testid="raid-clock-hour-input" type="number" min={0} max={23}
                  defaultValue={Math.floor(freeRoam.clock_minutes / 60) % 24}
                  onBlur={(e) => setClock(parseInt(e.target.value, 10) || 0, Math.floor(freeRoam.clock_minutes % 60))}
                  className="bg-black/30 border-white/10 h-7 text-xs w-14" placeholder="HH"
                />
                <span className="text-[#f1e0c5]/40">:</span>
                <Input
                  key={`raid-clock-minute-${freeRoam.clock_minutes}-${freeRoam.clock_day}`}
                  data-testid="raid-clock-minute-input" type="number" min={0} max={59}
                  defaultValue={Math.floor(freeRoam.clock_minutes % 60)}
                  onBlur={(e) => setClock(Math.floor(freeRoam.clock_minutes / 60) % 24, parseInt(e.target.value, 10) || 0)}
                  className="bg-black/30 border-white/10 h-7 text-xs w-14" placeholder="MM"
                />
                <Input
                  key={`raid-clock-day-${freeRoam.clock_day}`}
                  data-testid="raid-clock-day-input" type="number" min={1}
                  defaultValue={freeRoam.clock_day}
                  onBlur={(e) => setClockDay(parseInt(e.target.value, 10) || 1)}
                  className="bg-black/30 border-white/10 h-7 text-xs w-16" placeholder="Day"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <Button data-testid="raid-advance-1h-button" size="sm" variant="secondary" className="rounded-full text-[10px] h-7 px-2.5" onClick={() => advanceTime(60)}>+1 Hour</Button>
                <Button data-testid="raid-advance-6h-button" size="sm" variant="secondary" className="rounded-full text-[10px] h-7 px-2.5" onClick={() => advanceTime(6 * 60)}>+6 Hours</Button>
                <Button data-testid="raid-advance-1day-button" size="sm" variant="secondary" className="rounded-full text-[10px] h-7 px-2.5" onClick={() => advanceTime(24 * 60)}>+1 Day</Button>
              </div>
              <p className="text-[10px] text-[#f1e0c5]/30 italic">You control time for the whole Raid — movement never advances the clock, and every Branch sees this exact same time.</p>
            </>
          ) : (
            <p className="text-[10px] text-[#f1e0c5]/30 italic" data-testid="raid-clock-readonly-note">Only the Master GM controls the shared campaign clock.</p>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-[#f1e0c5]/70 flex items-center gap-1.5"><Swords className="w-3.5 h-3.5 text-[#D4AF37]" /> Free Roam Mode <InfoTip topic="free_roam" /></p>
        <Switch data-testid="gm-free-roam-switch" checked={freeRoam.enabled} onCheckedChange={toggle} />
      </div>

      {freeRoam.enabled && (
        <div className="space-y-2 bg-black/20 border border-white/10 rounded-xl p-3">
          {isRaid ? (
            <Select value={freeRoam.pace} onValueChange={setPace}>
              <SelectTrigger data-testid="gm-pace-select" className="bg-black/30 border-white/10 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="walk">Walk</SelectItem>
                <SelectItem value="hustle">Hustle (2x)</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs text-[#D4AF37]" data-testid="free-roam-clock-gm">
                <Clock className="w-3.5 h-3.5" /> {formatClock(freeRoam.clock_minutes, freeRoam.clock_day)}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="gm-clock-hour-input" type="number" min={0} max={23}
                  defaultValue={Math.floor(freeRoam.clock_minutes / 60) % 24}
                  onBlur={(e) => setClock(parseInt(e.target.value, 10) || 0, Math.floor(freeRoam.clock_minutes % 60))}
                  className="bg-black/30 border-white/10 h-7 text-xs w-14" placeholder="HH"
                />
                <span className="text-[#f1e0c5]/40">:</span>
                <Input
                  data-testid="gm-clock-minute-input" type="number" min={0} max={59}
                  defaultValue={Math.floor(freeRoam.clock_minutes % 60)}
                  onBlur={(e) => setClock(Math.floor(freeRoam.clock_minutes / 60) % 24, parseInt(e.target.value, 10) || 0)}
                  className="bg-black/30 border-white/10 h-7 text-xs w-14" placeholder="MM"
                />
                <Select value={freeRoam.pace} onValueChange={setPace}>
                  <SelectTrigger data-testid="gm-pace-select" className="bg-black/30 border-white/10 h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walk">Walk</SelectItem>
                    <SelectItem value="hustle">Hustle (2x)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {freeRoam.pace === "hustle" && freeRoam.hustle_seconds_remaining != null && (
            <p className="text-[10px] text-[#f1e0c5]/40" data-testid="gm-hustle-timer">Hustle stamina: {Math.ceil(freeRoam.hustle_seconds_remaining / 60)} min left</p>
          )}

          {(freeRoam.encounters || []).length === 0 && <p className="text-[10px] text-[#f1e0c5]/30 italic">No fights right now — players can start one anytime.</p>}
          {(freeRoam.encounters || []).map((enc) => (
            <div key={enc.id} className="bg-black/30 border border-[#D90429]/30 rounded-lg p-2 space-y-1.5" data-testid={`free-roam-encounter-${enc.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase text-[#D90429]">Round {enc.round}</p>
                <button data-testid={`end-encounter-${enc.id}`} onClick={() => endEncounter(enc.id)} className="text-[#f1e0c5]/40 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>
              </div>
              <div className="space-y-1">
                {(enc.order || []).map((o, i) => (
                  <div key={i} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${i === enc.turn_index ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "text-[#f1e0c5]/60"}`}>
                    <span>{o.name} {o.is_npc && "(NPC)"}</span>
                    <span>{o.initiative ?? "—"}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <Select value={pullNpcTarget[enc.id] || ""} onValueChange={(v) => setPullNpcTarget((p) => ({ ...p, [enc.id]: v }))}>
                  <SelectTrigger data-testid={`pull-npc-select-${enc.id}`} className="bg-black/30 border-white/10 h-7 text-xs flex-1"><SelectValue placeholder="Pull in an NPC…" /></SelectTrigger>
                  <SelectContent>{npcTokens.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                <button data-testid={`pull-npc-button-${enc.id}`} onClick={() => pullNpc(enc.id)} className="text-[#D4AF37] hover:text-white shrink-0"><UserPlus className="w-4 h-4" /></button>
              </div>
              <Button data-testid={`next-turn-button-${enc.id}`} size="sm" variant="secondary" className="w-full rounded-full gap-1.5" onClick={() => nextTurn(enc.id)}>
                <SkipForward className="w-3.5 h-3.5" /> Next Turn
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
